import React from "react";

export function renderNetraaAPMReleaseNotes3() {
  return <RenderNetraaAPMReleaseNotes />;
}

function RenderNetraaAPMReleaseNotes() {
  const releaseMetrics = [
    { label: "Modules Updated", value: "08" },
    { label: "Key Areas", value: "06" },
    { label: "Security & Governance", value: "1" },
    { label: "Impact Score", value: "9.2" },
  ];

  const releasePills = [
    "Alert & Problem Management",
    "Service Topology Enhancements",
    "Critical User Flow",
    "JBoss Domain Mode Agent Support",
  ];

  const majorFeatureEnhancements = [
    {
      number: 1,
      title: "Alert & Problem Management",
      subsections: [
        {
          title: "Alert Lifecycle Management",
          items: [
            "Alerts now follow a structured lifecycle with clear Open and Closed states, each tied to a unique Problem ID for consistent tracking and audit history.",
          ],
        },
        {
          title: "PID-Based Routing",
          items: [
            "Navigating to a Problem ID now automatically loads the full problem context in the UI, reducing manual lookup and speeding up triage.",
          ],
        },
        {
          title: "Endpoint Details Enhancement",
          items: [
            "The Endpoint Details API and UI have been enhanced to surface more complete and accurate endpoint information.",
          ],
        },
      ],
    },
    {
      number: 2,
      title: "Service Topology Enhancements",
      subsections: [
        {
          title: "Topology Refactor & API Optimization",
          items: [
            "The Service Topology module has been refactored for improved reliability and maintainability.",
            "The topology API now consolidates metrics into a single response, reducing the number of calls required to render a topology view and improving load performance.",
          ],
        },
        {
          title: "Dynamic Layers",
          items: [
            "Multiple dynamic layers have been added to the topology visualization, giving users a more detailed and flexible view of service relationships.",
          ],
        },
      ],
    },
    {
      number: 3,
      title: "Admin Configuration – UI/UX Enhancement",
      subsections: [
        {
          title: "Responsive Layout",
          items: [
            "Improved screen responsiveness for laptops, tablets, and desktops.",
            "Added smooth scrolling to ensure all content remains easily accessible.",
          ],
        },
        {
          title: "Navigation Improvements",
          items: [
            "Simplified sidebar navigation for quicker access to configuration modules.",
            "Enhanced top-bar navigation for a smoother user experience.",
          ],
        },
        {
          title: "UI Consistency",
          items: [
            "Standardized buttons, search fields, tables, and section headers across all Admin Configuration pages.",
            "Added consistent icons and visual styling for improved usability.",
          ],
        },
        {
          title: "Readability Enhancements",
          items: [
            "Improved spacing, alignment, and text handling to prevent overlapping and wrapping.",
            "Refined page layouts for a cleaner and more organized interface.",
          ],
        },
        {
          title: "Overall Experience",
          items: [
            "Delivered a more modern, responsive, and user-friendly Admin Configuration experience.",
          ],
        },
      ],
    },
    {
      number: 4,
      title: "Custom Dashboard – Critical Flow",
      subsections: [
        {
          title: "Critical Flow Charts",
          items: [
            "Enabled users to create charts specifically for critical flows.",
            "Implemented Funnel Chart visualization, giving users a new way to analyse stage-by-stage progression within the Custom Dashboard.",
          ],
        },
      ],
    },
    {
      number: 5,
      title: "Critical User Flow",
      subsections: [
        {
          title: "End-to-End Workflow Monitoring",
          items: [
            "Introduced the Critical User Flow feature, which allows users to configure key application workflows by defining applications, endpoints, and execution steps.",
            "This feature helps monitor the complete journey of critical business processes end-to-end, improving visibility into how key transactions perform in production.",
          ],
        },
      ],
    },
    {
      number: 6,
      title: "Custom Dashboard – External & Dynamic Metrics Integration",
      subsections: [
        {
          title: "External Metrics Ingestion",
          items: [
            "Enabled ingestion of external and custom metric data directly into the Custom Dashboard.",
          ],
        },
        {
          title: "Dynamic Metric APIs",
          items: [
            "Developed APIs to accept dynamic metric data, store it in Elastic search, register previously unavailable metrics, and map them to Custom Dashboard configurations.",
          ],
        },
        {
          title: "UI Integration",
          items: [
            "Integrated the new APIs with the Custom Dashboard UI, enabling newly ingested metrics to be visualized on dashboards.",
          ],
        },
        {
          title: "Flexible Data Structures",
          items: [
            "Enhanced Elasticsearch ingestion to support dynamic data structures, removing dependency on predefined metric structures.",
          ],
        },
      ],
    },
    {
      number: 7,
      title: "Windows Infrastructure Dashboard Enhancement",
      subsections: [
        {
          title: "UI Parity with Linux Dashboard",
          items: [
            "Revamped the Windows Dashboard UI and chart behaviour to maintain consistency with the Linux Dashboard.",
          ],
        },
        {
          title: "Threshold-Based Visualization",
          items: [
            "Implemented threshold-based visualization and colour coding for CPU Usage, Memory RAM, Memory Swap, CPU Load, Network Bandwidth, File Systems, and Disk Usage.",
          ],
        },
        {
          title: "Disk Usage Improvements",
          items: [
            "Enhanced Disk Usage KPI and detail tables with threshold breach indicators.",
          ],
        },
      ],
    },
    {
      number: 8,
      title: "Java Agent Enhancement",
      subsections: [
        {
          title: "JBoss Domain Mode Support",
          items: [
            "Added Netraa Java Agent support for JBoss running in Domain Mode, enabling seamless monitoring and instrumentation of applications deployed in JBoss Domain Mode environments.",
          ],
        },
      ],
    },
  ];

  const notes = [
    "Alert & Problem Management: structured alert lifecycle with Open/Closed states and unique Problem IDs, PID-based routing for faster triage, and enhanced Endpoint Details API and UI.",
    "Service Topology Enhancements: refactored topology module with a consolidated metrics API for improved performance, plus multiple new dynamic visualization layers.",
    "Admin Configuration – UI/UX Enhancement: responsive layouts, simplified navigation, consistent styling, and improved readability across all Admin Configuration pages.",
    "Custom Dashboard – Critical Flow: new critical flow charts, including Funnel Chart visualization for stage-by-stage progression analysis.",
    "Critical User Flow: end-to-end monitoring of key application workflows by configuring applications, endpoints, and execution steps.",
    "Custom Dashboard – External & Dynamic Metrics Integration: ingestion of external and dynamic metric data via new APIs, Elasticsearch-backed storage, and full Custom Dashboard UI integration.",
    "Windows Infrastructure Dashboard Enhancement: UI parity with the Linux Dashboard, threshold-based visualization and colour coding, and improved Disk Usage KPIs.",
    "Java Agent Enhancement: added support for JBoss running in Domain Mode.",
  ];

  return (
    <div>
      <main className="transition-opacity duration-300">
        <div className="mx-auto">
          <section className="relative scroll-mt-24" id="release-details">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <span className="h-2 w-2 rounded-full bg-orange-500" />
                Release Notes 2026.07
              </div>

              <div className="flex items-center justify-between gap-4">
                <h1 className="text-3xl font-black tracking-tight sm:text-4xl">
                  <span className="bg-gradient-to-r from-orange-500 via-red-500 to-rose-600 bg-clip-text text-transparent">
                    Netraa APM Release Notes
                  </span>
                </h1>
              </div>

              <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg dark:text-slate-300">
                Netraa APM – Release Notes
              </p>
            </div>
          </section>

          <section className="scroll-mt-24 px-0 py-4" id="intro">
            <div className="rounded-3xl border border-orange-200/70 bg-gradient-to-br from-orange-50 via-white to-white p-6 shadow-sm dark:border-orange-500/15 dark:from-orange-500/10 dark:via-slate-950/30 dark:to-slate-950/30">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/25">
                  ✦
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                    Introduction
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    What this release brings to the platform.
                  </p>
                </div>
              </div>

              <p className="text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base">
                This release strengthens Netraa APM across six key areas,
                spanning alert and problem management, service topology,
                admin configuration UI/UX, custom dashboards, critical user
                flow monitoring, and infrastructure dashboards. Highlights
                include structured alert lifecycle management with PID-based
                routing, a refactored service topology with dynamic layers,
                a redesigned Admin Configuration experience, Funnel Chart
                visualization, the new Critical User Flow feature, external
                and dynamic metrics ingestion into Custom Dashboards, Windows
                dashboard parity with Linux, and JBoss Domain Mode agent
                support.
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {releasePills.map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-200"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-10 w-full scroll-mt-24" id="analytics">
            <div className="mx-auto">
              <h4 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
                Release Analytics
              </h4>

              <div
                className="
      glass-card
      rounded-2xl
      border border-slate-200 dark:border-white/10
      bg-white/90 p-5 backdrop-blur-xl
      transition-all duration-500
      hover:-translate-y-1 hover:shadow-2xl
      dark:bg-white/5
      sm:p-6
    "
              >
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {releaseMetrics.map((item, i) => (
                    <div
                      key={i}
                      className="
              rounded-xl
              border border-slate-200 bg-white p-3
              shadow-sm
              transition-all duration-300
              hover:-translate-y-1 hover:scale-[1.02] hover:shadow-md
              dark:border-white/10 dark:bg-white/5
            "
                    >
                      <div className="mb-1 text-[11px] text-slate-500 dark:text-slate-400">
                        {item.label}
                      </div>

                      <div
                        className="
              text-lg font-bold text-orange-600
              dark:bg-gradient-to-r dark:from-orange-500 dark:to-rose-600
              dark:bg-clip-text dark:text-transparent
            "
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="mb-1 flex justify-between text-sm text-slate-700 dark:text-slate-300">
                      <span>Deployment Readiness</span>
                      <span className="font-semibold text-orange-500">98%</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="
              h-full w-[98%]
              bg-gradient-to-r from-orange-500 to-rose-600
              shadow-[0_0_8px_rgba(255,120,120,0.4)]
              transition-all duration-700
            "
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 flex justify-between text-sm text-slate-700 dark:text-slate-300">
                      <span>Health Meter</span>
                      <span className="font-semibold text-rose-500">Optimal</span>
                    </div>

                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        className="
              h-full w-full
              bg-gradient-to-r from-orange-500 to-rose-600
              shadow-[0_0_8px_rgba(255,120,120,0.4)]
              transition-all duration-700
            "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12 mt-12" id="overview">
            <h2 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
              Release Details
            </h2>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-slate-50 dark:bg-white/5">
                  <tr>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Release Month
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Organization
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Prepared By
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  <tr className="bg-white dark:bg-transparent">
                    <td className="px-5 py-4 font-medium text-orange-600 dark:text-orange-400 border border-slate-200 dark:border-white/10">
                      July 2026
                    </td>
                    <td className="px-5 py-4 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10">
                      ApMoSys Products Pvt. Ltd.
                    </td>
                    <td className="px-5 py-4j 2 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-white/10">
                      Netraa APM Product Team
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="scroll-mt-24" id="enhancements">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  Major Feature Enhancements
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Exact feature list from the release notes.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {majorFeatureEnhancements.map((section) => (
                <article
                  key={section.title}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/60"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-red-500 text-sm font-bold text-white shadow-md shadow-orange-500/20">
                      {section.number}
                    </div>

                    <div className="min-w-0 w-full">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        {section.title}
                      </h3>

                      <div className="mt-3 space-y-4">
                        {section.subsections.map((sub) => (
                          <div key={sub.title}>
                            <h4 className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                              {sub.title}
                            </h4>
                            <ul className="mt-2 space-y-2">
                              {sub.items.map((point) => (
                                <li
                                  key={point}
                                  className="flex gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
                                >
                                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="py-8 scroll-mt-24" id="notes">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                Additional Notes
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Extra content kept in the layout.
              </p>

              <ul className="mt-6 space-y-4">
                {notes.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500" />
                    <span className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

              <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 p-6 text-white shadow-lg">
                <h3 className="text-xl font-bold">Release Summary</h3>

                <p className="mt-3 text-sm leading-6 text-white/90">
                  This release strengthens Netraa APM across six key areas,
                  delivering alert lifecycle management, service topology
                  improvements, a redesigned Admin Configuration experience,
                  new Custom Dashboard capabilities, the Critical User Flow
                  feature, and infrastructure dashboard enhancements.
                </p>

                <div className="mt-5 text-sm font-semibold">
                  Status: Major enhancements and platform improvements
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}